// ============================================================================
// @torre/expenses — Plataformas vs. infraestructura
// ============================================================================
// Dos cosas distintas que antes vivían juntas en la categoría `plataformas`:
//
//   · PLATAFORMA      → herramienta con la que trabaja el equipo (correo y
//                       documentos, repositorios, chat, asistentes de código,
//                       CRM). Suele cobrarse por asiento o tarifa fija. Es
//                       gasto de operación.
//   · INFRAESTRUCTURA → lo que corre o consume el producto en producción para
//                       dar servicio a los clientes (nube y hosting, bases de
//                       datos, dominios y DNS, APIs de mapas, envío de correo,
//                       SMS y voz, APIs de IA, telemetría, monitoreo). Suele
//                       cobrarse por consumo. Es costo del servicio.
//
// El tipo sale de la categoría de gasto: `infraestructura` es infraestructura y
// cualquier otra categoría es plataforma. Así los cargos ligados a una
// plataforma heredan su tipo con su categoría, sin un campo aparte. Lógica pura.
// ============================================================================

import { round2, type Expense, type ExpenseCategory, type Platform, type PlatformReport } from "./index";
import { platformBillingOverview, type PlatformBillingOverview } from "./platformBilling";

export const PLATFORM_KINDS = ["plataforma", "infraestructura"] as const;
export type PlatformKind = (typeof PLATFORM_KINDS)[number];

export const PLATFORM_KIND_LABELS: Record<PlatformKind, { es: string; en: string }> = {
  plataforma: { es: "Plataforma", en: "Platform" },
  infraestructura: { es: "Infraestructura", en: "Infrastructure" },
};

/** Categoría de gasto que marca la infraestructura. */
export const INFRASTRUCTURE_CATEGORY: ExpenseCategory = "infraestructura";
/** Categoría por defecto de una plataforma (herramienta del equipo). */
export const PLATFORM_CATEGORY: ExpenseCategory = "plataformas";

export const isPlatformKind = (value: unknown): value is PlatformKind => PLATFORM_KINDS.includes(value as PlatformKind);

/** Tipo de una plataforma del catálogo, según su categoría de gasto. */
export function platformKindOf(p: Pick<Platform, "category">): PlatformKind {
  return p.category === INFRASTRUCTURE_CATEGORY ? "infraestructura" : "plataforma";
}

/**
 * Categoría de gasto al elegir el tipo. Infraestructura siempre es
 * `infraestructura`; una plataforma conserva su categoría si ya tenía otra
 * (marketing, servicios…) y solo deja `infraestructura` por `plataformas`.
 */
export function categoryForPlatformKind(kind: PlatformKind, current?: ExpenseCategory | null): ExpenseCategory {
  if (kind === "infraestructura") return INFRASTRUCTURE_CATEGORY;
  return current && current !== INFRASTRUCTURE_CATEGORY ? current : PLATFORM_CATEGORY;
}

/** Encabezado de Pago de plataformas, separado por tipo. */
export function platformKindOverview(
  platforms: readonly Platform[],
  rates: Record<string, number> = {},
  base = "MXN",
): Record<PlatformKind, PlatformBillingOverview> {
  const of = (kind: PlatformKind) => platformBillingOverview(platforms.filter((p) => platformKindOf(p) === kind), rates, base);
  return { plataforma: of("plataforma"), infraestructura: of("infraestructura") };
}

export interface PlatformSpendByKindMonth {
  period: string;
  plataforma: number;
  infraestructura: number;
}

export interface PlatformSpendByKind {
  /** Pagado por mes y tipo, en moneda base, en el rango del reporte (hasta el mes en curso). */
  monthly: PlatformSpendByKindMonth[];
  /** Pagado en el año en curso hasta hoy, por tipo. */
  ytd: Record<PlatformKind, number>;
  /** Parte de la infraestructura en lo pagado del año (0–1); `null` si no se ha pagado nada. */
  infrastructureShare: number | null;
}

/** Lo pagado mes a mes separado en plataformas e infraestructura. */
export function platformSpendByKind(report: readonly PlatformReport[], platforms: readonly Pick<Platform, "id" | "category">[], today: string): PlatformSpendByKind {
  const kindById = new Map(platforms.map((p) => [p.id, platformKindOf(p)]));
  const current = today.slice(0, 7);
  const year = today.slice(0, 4);
  const byPeriod = new Map<string, PlatformSpendByKindMonth>();
  const ytd: Record<PlatformKind, number> = { plataforma: 0, infraestructura: 0 };
  for (const r of report) {
    const kind = kindById.get(r.platformId) || "plataforma";
    for (const m of r.months) {
      if (m.period > current) continue;
      const row = byPeriod.get(m.period) || { period: m.period, plataforma: 0, infraestructura: 0 };
      row[kind] += m.paid;
      byPeriod.set(m.period, row);
      if (m.period.startsWith(`${year}-`)) ytd[kind] += m.paid;
    }
  }
  const monthly = [...byPeriod.values()]
    .sort((a, b) => a.period.localeCompare(b.period))
    .map((m) => ({ period: m.period, plataforma: round2(m.plataforma), infraestructura: round2(m.infraestructura) }));
  ytd.plataforma = round2(ytd.plataforma);
  ytd.infraestructura = round2(ytd.infraestructura);
  const total = ytd.plataforma + ytd.infraestructura;
  return { monthly, ytd, infrastructureShare: total > 0 ? round2(ytd.infraestructura / total) : null };
}

/** Un gasto es de infraestructura, de plataformas, o ninguno de los dos (nómina, viajes…). */
export function expenseKindOf(e: Pick<Expense, "category">): PlatformKind | null {
  if (e.category === INFRASTRUCTURE_CATEGORY) return "infraestructura";
  return e.category === PLATFORM_CATEGORY ? "plataforma" : null;
}
